// Paddle Billing JS v2 — overlay checkout helper
// Docs: https://developer.paddle.com/paddlejs/overview

import { trackEvent } from './posthog';

declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    Paddle?: any;
  }
}

let initialized = false;

/**
 * Dynamically loads the Paddle.js v2 script and initializes it.
 * Safe to call multiple times — only loads once.
 */
export function loadPaddle(): Promise<void> {
  if (initialized) return Promise.resolve();
  if (window.Paddle) {
    // Already loaded by a previous call
    initialized = true;
    return Promise.resolve();
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = 'https://cdn.paddle.com/paddle/v2/paddle.js';
    script.async = true;
    script.onload = () => {
      const clientToken = import.meta.env.VITE_PADDLE_CLIENT_TOKEN as string;
      if (!clientToken) {
        console.error('[Atilum] Missing VITE_PADDLE_CLIENT_TOKEN env var');
      }
      // For non-live tokens (test_ / sandbox_), tell Paddle.js to use sandbox
      if (!clientToken?.startsWith('live_')) {
        window.Paddle?.Environment.set('sandbox');
      }
      window.Paddle?.Initialize({
        token: clientToken,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        eventCallback: (event: any) => {
          if (event?.name === 'checkout.completed') {
            trackEvent('checkout_completed', { transaction_id: event.data?.transaction_id });
            const txnId = event.data?.transaction_id;
            const customerId = event.data?.customer?.id;
            if (txnId) {
              sessionStorage.setItem('atilum_paddle', JSON.stringify({
                transactionId: txnId,
                customerId: customerId ?? null,
              }));
              sessionStorage.setItem('atilum_paddle_txn', txnId); // keep for compat
            }
            // Navigate to post-checkout (overlay mode doesn't use successUrl)
            const url = new URL('/post-checkout', window.location.origin);
            if (txnId) url.searchParams.set('_ptxn', txnId);
            window.location.href = url.toString();
          }
        },
      });
      initialized = true;
      resolve();
    };
    script.onerror = (err) => {
      console.error('[Atilum] Failed to load Paddle.js', err);
      reject(err);
    };
    document.head.appendChild(script);
  });
}

export interface PricePreviewItem {
  priceId: string;
  formatted: string;
  currencyCode: string;
  unitAmountRaw: string;
}

/**
 * Returns localised price strings for the given price IDs using Paddle.PricePreview.
 */
export async function previewPrices(priceIds: string[]): Promise<PricePreviewItem[]> {
  await loadPaddle();
  if (!window.Paddle) return [];

  const result = await window.Paddle.PricePreview({
    items: priceIds.map(id => ({ priceId: id, quantity: 1 })),
  });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (result.data?.details?.lineItems ?? []).map((li: any) => ({
    priceId: li.price.id,
    formatted: li.formattedTotals.total,
    currencyCode: result.data.currencyCode,
    unitAmountRaw: li.price.unitPrice.amount,
  }));
}

interface OpenCheckoutOptions {
  /** Supabase user UUID — passed as custom_data to identify the user in the webhook. Optional for pre-signup checkout. */
  supabaseUserId?: string;
  /** Pre-fill the checkout email field */
  userEmail?: string;
  /** Override the price ID from env var */
  priceId?: string;
}

/**
 * Opens the Paddle overlay checkout.
 * After successful payment Paddle redirects to /post-checkout?_ptxn=<txn_id>.
 */
export async function openPaddleCheckout(options: OpenCheckoutOptions): Promise<void> {
  await loadPaddle();

  const priceId = options.priceId ?? (import.meta.env.VITE_PADDLE_PRICE_ID_MONTHLY as string);

  if (!priceId) {
    console.error('[Atilum] Missing VITE_PADDLE_PRICE_ID_MONTHLY env var');
    return;
  }

  if (!window.Paddle) {
    console.error('[Atilum] Paddle not loaded');
    return;
  }

  trackEvent('checkout_opened', { priceId });
  window.Paddle.Checkout.open({
    items: [{ priceId, quantity: 1 }],
    ...(options.supabaseUserId && {
      customData: {
        supabase_user_id: options.supabaseUserId,
      },
    }),
    ...(options.userEmail && {
      customer: { email: options.userEmail },
    }),
    settings: {
      displayMode: 'overlay',
      theme: 'light',
      locale: navigator.language || 'fr',
      successUrl: `${window.location.origin}/post-checkout`,
    },
  });
}
